import React from "react";
import { createPortal } from "react-dom";
import { Camera, Check, Globe, Pencil, Plus, Settings, Trash2, X } from "lucide-react";
import DuplicateMenuContent from "../ui/DuplicateMenuContent";
import Tooltip from "../ui/Tooltip";
import { useSceneStore } from "../../stores/sceneStore";
import {
  INSPECTOR_CHROME_ICON_BUTTON_CLASS,
  INSPECTOR_CHROME_ICON_DELETE_CLASS,
} from "../../utils/inspectorChromeButtonClasses";
import { nextAvailableCameraName } from "../../utils/nextAvailableCameraName";
import { getTransformEditorBlockProps, usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import { MULTI_MESH_TRANSFORM_SAME_BODY_WARNING } from "../../utils/visualMeshTransformTargets";
import { INSPECTOR_TITLE_TYPE_LABEL } from "./inspectorTargetUtils";
import { InspectorChromeAutofocusButton } from "./InspectorChromeAutofocusButton";
import { InspectorPanelIcon } from "./inspectorPanelIdentity";
import QuickNavReparentMenu, { ReparentMenuIcon } from "./QuickNavReparent";
import TransformGizmoHeaderActions from "./TransformGizmoHeaderActions";
import { useTransformGizmoControls } from "./useTransformGizmoControls";
import { dispatchViewportCameraVisibility } from "../draggable-panels/scene-inventory/sceneInventoryViewportEvents";

const INSPECTOR_NEW_CAMERA_DEFAULT_WIDTH = 800;
const INSPECTOR_NEW_CAMERA_DEFAULT_HEIGHT = 800;

function MultiBodySelectionHeaderActions() {
  const authoringLocked = usePhysicsAuthoringLock();
  const transformEditorBlock = getTransformEditorBlockProps(authoringLocked);
  const { showPositionGizmo, showRotationGizmo, togglePositionGizmo, toggleRotationGizmo } =
    useTransformGizmoControls({
      disabled: transformEditorBlock.disabled,
      enableShortcuts: true,
    });

  return (
    <div className="flex shrink-0 items-center gap-1">
      <div
        className="flex items-center"
        data-no-panel-drag="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <TransformGizmoHeaderActions
          disabled={transformEditorBlock.disabled}
          disabledTitle={transformEditorBlock.title}
          showPositionGizmo={showPositionGizmo}
          showRotationGizmo={showRotationGizmo}
          onTogglePositionGizmo={togglePositionGizmo}
          onToggleRotationGizmo={toggleRotationGizmo}
        />
      </div>
    </div>
  );
}

function MultiMeshSelectionHeaderActions({ canTransform, transformDisabledTitle }) {
  const authoringLocked = usePhysicsAuthoringLock();
  const transformEditorBlock = getTransformEditorBlockProps(authoringLocked);
  const transformDisabled = transformEditorBlock.disabled || !canTransform;
  const transformTitle = transformEditorBlock.disabled
    ? transformEditorBlock.title
    : transformDisabledTitle;
  const { showPositionGizmo, showRotationGizmo, togglePositionGizmo, toggleRotationGizmo } =
    useTransformGizmoControls({
      disabled: transformDisabled,
      enableShortcuts: canTransform,
    });

  return (
    <div className="flex shrink-0 items-center gap-1">
      <div
        className="flex items-center"
        data-no-panel-drag="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <TransformGizmoHeaderActions
          disabled={transformDisabled}
          disabledTitle={transformTitle}
          showPositionGizmo={showPositionGizmo}
          showRotationGizmo={showRotationGizmo}
          onTogglePositionGizmo={togglePositionGizmo}
          onToggleRotationGizmo={toggleRotationGizmo}
        />
      </div>
    </div>
  );
}

export default function InspectorPanelTitle({
  activePanel,
  allBodies,
  allWorldBodies,
  cancelTitleRename,
  commitTitleRename,
  handleInspectorDelete,
  handleInspectorDuplicate,
  inspectorDeleteDisabled,
  inspectorDeleteLabel,
  inspectorDuplicateDisabled,
  inspectorReparentConfig,
  inspectorReparentMenuPosition,
  inspectorReparentMenuRef,
  inspectorReparentOpen,
  inspectorReparentRef,
  isMobile,
  isPhysicsRunning,
  isRenamingTitle,
  mobileScenePeekCompact,
  renameTarget,
  resolvedSelectedGeom,
  sceneActions,
  selectCamera,
  selectedCamera,
  selectedVisualMeshTransformState,
  setInspectorReparentMenuPosition,
  setInspectorReparentOpen,
  setLastSelectionSource,
  setMobileScenePeekExpanded,
  setTitleDraftName,
  showInspectorDuplicate,
  startInspectorRename,
  titleDraftName,
  titleInfo,
  missingSelection,
}) {
  if (missingSelection) {
    return (
      <div className="flex items-center gap-2 min-w-0">
        <InspectorPanelIcon activePanel={activePanel} className="h-4 w-4" />
        <div className="min-w-0 flex-1 truncate text-base font-semibold leading-tight text-gray-100">
          Selection no longer exists
        </div>
      </div>
    );
  }

  if (!titleInfo) {
    return (
      <div className="flex items-center gap-2">
        <InspectorPanelIcon activePanel={activePanel} />
        <span className="text-base font-semibold text-gray-100">Inspector</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-w-0 w-full gap-1">
      <div className="flex min-w-0 w-full items-center gap-2">
        <InspectorPanelIcon activePanel={activePanel} className="h-4 w-4" />
        <div className="flex min-w-0 flex-1 items-center gap-1">
          {isRenamingTitle && renameTarget && !isPhysicsRunning ? (
            <>
              <input
                data-no-panel-drag="true"
                type="text"
                value={titleDraftName}
                onChange={(event) => setTitleDraftName(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitTitleRename();
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    cancelTitleRename();
                  }
                }}
                autoFocus={!isMobile}
                className="w-full min-w-0 rounded-sm border border-transparent bg-transparent px-1 py-0 text-base font-semibold leading-tight text-gray-100 outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-500/60"
              />
              <Tooltip content="Save name">
                <button
                  data-no-panel-drag="true"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    commitTitleRename();
                  }}
                  className={`${INSPECTOR_CHROME_ICON_BUTTON_CLASS} h-6 w-6`}
                  aria-label="Save name"
                  type="button"
                >
                  <Check className="w-3 h-3" />
                </button>
              </Tooltip>
              <Tooltip content="Cancel rename">
                <button
                  data-no-panel-drag="true"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    cancelTitleRename();
                  }}
                  className={`${INSPECTOR_CHROME_ICON_BUTTON_CLASS} h-6 w-6`}
                  aria-label="Cancel rename"
                  type="button"
                >
                  <X className="w-3 h-3" />
                </button>
              </Tooltip>
            </>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <div className="min-w-0 flex-1 truncate text-base font-semibold leading-tight text-gray-100">
                {titleInfo.name}
              </div>
              <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                {INSPECTOR_TITLE_TYPE_LABEL[titleInfo.type] ?? titleInfo.type}
              </span>
            </div>
          )}
        </div>
      </div>
      {!isRenamingTitle ? (
        <div className="flex items-center justify-start gap-1 min-w-0 w-full">
          {mobileScenePeekCompact && !activePanel ? (
            <Tooltip content="Show all properties">
              <button
                data-no-panel-drag="true"
                type="button"
                aria-label="Show all properties"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  setMobileScenePeekExpanded(true);
                }}
                className={INSPECTOR_CHROME_ICON_BUTTON_CLASS}
              >
                <Settings className="w-3.5 h-3.5" aria-hidden />
              </button>
            </Tooltip>
          ) : null}
          {activePanel === "camera" && selectedCamera ? (
            <>
              <Tooltip content="Open Camera Probe">
                <button
                  data-no-panel-drag="true"
                  type="button"
                  aria-label="Open Camera Probe"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    window.dispatchEvent(
                      new CustomEvent("open-camera-probe", {
                        detail: { cameraUuid: selectedCamera },
                      })
                    );
                  }}
                  className={INSPECTOR_CHROME_ICON_BUTTON_CLASS}
                >
                  <Camera className="w-3.5 h-3.5" aria-hidden />
                </button>
              </Tooltip>
              {!isPhysicsRunning ? (
                <Tooltip content="New camera">
                  <button
                    data-no-panel-drag="true"
                    type="button"
                    aria-label="New camera"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      const name = nextAvailableCameraName(useSceneStore.getState().cameras, {
                        kind: "world",
                      });
                      const newUuid = sceneActions.addCamera({
                        name,
                        kind: "fixed",
                        position: [0, 0.5, 2],
                        orientation: [1, 0, 0, 0],
                        fovy: 65,
                        width: INSPECTOR_NEW_CAMERA_DEFAULT_WIDTH,
                        height: INSPECTOR_NEW_CAMERA_DEFAULT_HEIGHT,
                        fps: 8,
                      });
                      if (newUuid) {
                        dispatchViewportCameraVisibility(true);
                        setLastSelectionSource("sceneTree");
                        selectCamera(newUuid);
                      }
                    }}
                    className={INSPECTOR_CHROME_ICON_BUTTON_CLASS}
                  >
                    <Plus className="w-3.5 h-3.5" aria-hidden />
                  </button>
                </Tooltip>
              ) : null}
            </>
          ) : null}
          {!isMobile ? (
            <InspectorChromeAutofocusButton
              activePanel={activePanel}
              resolvedSelectedGeom={resolvedSelectedGeom}
            />
          ) : null}
          {renameTarget && !isPhysicsRunning ? (
            <Tooltip content={`Rename ${titleInfo.type}`}>
              <button
                data-no-panel-drag="true"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  startInspectorRename();
                }}
                className={INSPECTOR_CHROME_ICON_BUTTON_CLASS}
                aria-label={`Rename ${titleInfo.type}`}
                type="button"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </Tooltip>
          ) : null}
          {activePanel === "multiBody" ? (
            <MultiBodySelectionHeaderActions />
          ) : activePanel === "multiMesh" ? (
            <MultiMeshSelectionHeaderActions
              canTransform={selectedVisualMeshTransformState.canTransform}
              transformDisabledTitle={
                selectedVisualMeshTransformState.reason || MULTI_MESH_TRANSFORM_SAME_BODY_WARNING
              }
            />
          ) : null}
          {!isPhysicsRunning ? (
            <>
              {inspectorReparentConfig.visible ? (
                <div
                  ref={inspectorReparentRef}
                  className="relative"
                  data-no-panel-drag="true"
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <Tooltip content={inspectorReparentConfig.label}>
                    <button
                      data-no-panel-drag="true"
                      type="button"
                      aria-label={inspectorReparentConfig.label}
                      disabled={inspectorReparentConfig.disabled}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (inspectorReparentConfig.disabled) return;
                        const rect = event.currentTarget.getBoundingClientRect();
                        setInspectorReparentMenuPosition({
                          left: Math.max(8, Math.min(rect.left, window.innerWidth - 236)),
                          top: Math.min(rect.bottom + 4, window.innerHeight - 240),
                        });
                        setInspectorReparentOpen((open) => !open);
                      }}
                      className={`${INSPECTOR_CHROME_ICON_BUTTON_CLASS} disabled:!pointer-events-auto`}
                    >
                      <ReparentMenuIcon className="w-3.5 h-3.5" />
                    </button>
                  </Tooltip>
                  {inspectorReparentOpen &&
                  !inspectorReparentConfig.disabled &&
                  inspectorReparentMenuPosition
                    ? createPortal(
                        <div
                          ref={inspectorReparentMenuRef}
                          className="fixed z-[50000] min-w-[220px]"
                          style={{
                            left: inspectorReparentMenuPosition.left,
                            top: inspectorReparentMenuPosition.top,
                          }}
                          data-no-panel-drag="true"
                        >
                          <QuickNavReparentMenu
                            options={inspectorReparentConfig.options || []}
                            bodies={allBodies}
                            worldBodies={allWorldBodies}
                            onSelect={inspectorReparentConfig.onSelect}
                            className="rounded-md border border-gray-600 p-0.5 shadow-xl"
                            leadingContent={
                              inspectorReparentConfig.canMoveToWorld ? (
                                <button
                                  type="button"
                                  onClick={() => inspectorReparentConfig.onSelect(null)}
                                  className="w-full text-left rounded-sm px-2 py-1.5 text-sm text-gray-200 hover:bg-[var(--panel-surface-hover)] transition-colors"
                                >
                                  <span className="inline-flex items-center gap-2">
                                    <Globe className="w-4 h-4" aria-hidden="true" />
                                    World
                                  </span>
                                </button>
                              ) : null
                            }
                          />
                        </div>,
                        document.body
                      )
                    : null}
                </div>
              ) : null}
              {showInspectorDuplicate ? (
                <div onMouseDown={(event) => event.stopPropagation()}>
                  <DuplicateMenuContent
                    size="compact"
                    useCopyIconForBodyAndCollision
                    disabled={inspectorDuplicateDisabled}
                    physicsRunning={false}
                    buttonExtraProps={{
                      "data-no-panel-drag": "true",
                      onMouseDown: (event) => event.stopPropagation(),
                    }}
                    onDuplicateBody={
                      activePanel === "body" || activePanel === "object"
                        ? handleInspectorDuplicate
                        : null
                    }
                    onDuplicateCollision={activePanel === "geom" ? handleInspectorDuplicate : null}
                    onDuplicateItem={
                      activePanel === "joint" ||
                      activePanel === "visualGeom" ||
                      activePanel === "site" ||
                      activePanel === "equality" ||
                      activePanel === "tendon" ||
                      activePanel === "multiBody" ||
                      activePanel === "multiMesh"
                        ? handleInspectorDuplicate
                        : null
                    }
                    duplicateItemLabel={
                      activePanel === "joint"
                        ? "Duplicate joint"
                        : activePanel === "visualGeom"
                          ? "Duplicate visual"
                          : activePanel === "site"
                            ? "Duplicate site"
                            : activePanel === "equality"
                              ? "Duplicate constraint"
                              : activePanel === "tendon"
                                ? "Duplicate tendon"
                                : activePanel === "multiBody"
                                  ? "Duplicate bodies"
                                  : activePanel === "multiMesh"
                                    ? "Duplicate visuals"
                                    : "Duplicate"
                    }
                  />
                </div>
              ) : null}
              <Tooltip content={inspectorDeleteLabel}>
                <button
                  data-no-panel-drag="true"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleInspectorDelete();
                  }}
                  disabled={inspectorDeleteDisabled}
                  className={`${INSPECTOR_CHROME_ICON_DELETE_CLASS} disabled:!pointer-events-auto`}
                  aria-label={inspectorDeleteLabel}
                  type="button"
                >
                  <Trash2 className="w-3.5 h-3.5" aria-hidden />
                </button>
              </Tooltip>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
